<template>
<td :colspan="alternateStoreHeaders.length" style="padding:0px;">
  <v-container class="ma-0 pa-0" fluid>
    <v-data-table
      :headers="alternateStoreHeaders"
      :items="localItems"
      :item-class="getItemClass"
      class="table"
      :loading="loading"
      loader-height="2"
      dense
      hide-default-header
      hide-default-footer>
      <template v-if="tenant=='alliance-retail-group'" #[`item.is_shipper`]="{ item }">
        <span v-if="item.is_shipper">
          <v-btn icon color="primary" @click.stop="selectedItem = item; showComponents = true">
            <v-icon>mdi-view-list</v-icon>
          </v-btn>
        </span>
      </template>
      <template #[`item.promotion_price`]="{ item, index }">
        <td class="px-6">
          <v-row class="my-1 px-0">
            <v-col cols="3" 
              class="pa-0 flex-grow-0" >
              <v-text-field
                :readonly="isReadOnlyUser"
                v-model.number="item.multiplier"
                v-bind="{'data-row': index, 'data-field': 'alt_multiplier'}"
                :rules="multiplierRules"
                dense
                outlined
                hide-details
                solo
                type="number"
                :background-color="srpFieldBg(item)"
                :disabled="isEditingDisabled"
                @input="onSubItemChange(item, 'multiplier', $event)"
                @keydown.enter="handleEnterKey(index, 'alt_multiplier', $event)">
              </v-text-field>
            </v-col>
            <v-icon size="20" class="px-2">mdi-at</v-icon>
            <v-col class="pa-0 flex-grow-1">
              <v-text-field
                :readonly="isReadOnlyUser" 
                v-model.number="item.promotion_price"
                v-bind="{'data-row': index, 'data-field': 'alt_promotion_price'}"
                :rules="srpRules"
                prefix="$"
                class="px-0"
                dense
                outlined
                hide-details
                solo
                step=".01"
                type="number"
                :background-color="srpFieldBg(item)"
                :disabled="isEditingDisabled"
                @input="onSubItemChange(item, 'promotion_price', $event)"
                @blur="onAltSrpBlur(item)"
                @keydown.enter="handleEnterKey(index, 'alt_promotion_price', $event)">
              </v-text-field>
            </v-col>
            <v-col class="pa-0 flex-grow-0">
              <v-tooltip top>
                <template v-slot:activator="{ on }">
                  <v-btn 
                    v-on="on"
                    class="mt-5"
                    x-small
                    icon
                    :disabled="isEditingDisabled || isReadOnlyUser"
                    :style="{ visibility: item.linked_group != null ? 'visible' : 'hidden' }"
                    @click="showLinkedGroupModal(item)">
                    <v-icon color="blue darken-2">mdi-alpha-l-circle-outline</v-icon>
                  </v-btn>
                </template>
                <span>Apply SRP to ALL Linked Group items</span>
              </v-tooltip>
            </v-col>
            <v-col class="pa-0 flex-grow-0">
              <v-tooltip top>
                <template v-slot:activator="{ on }">
                <v-btn
                  v-on="on"
                  icon
                  :disabled="isEditingDisabled"
                  :style="{ visibility: changes.some(change => change.id === item.id) ? 'visible' : 'hidden' }"
                  @click="resetRow(item)">
                  <v-icon>mdi-refresh-circle</v-icon>
                </v-btn>
                </template>
                <span>Reset Values</span>
              </v-tooltip>
            </v-col>
          </v-row>
        </td>
      </template>
      <template #[`header.data-table-select`]="{ on, props }">
        <v-simple-checkbox :ripple="false" :disabled="isEditingDisabled" v-on="on" v-bind="props" dark />
      </template>
      <template #[`item.group_manager_modified`]="{ item, on }">
        <v-simple-checkbox
          :value="item.group_manager_modified === true"
          :ripple="false"
          v-on="on"
        />
      </template>
      <template #[`item.excluded`]="{ item, on }">
        <v-tooltip :disabled="!getExclusionReason(item)" top>
          <template #activator="{ on: tooltipOn }">
            <div class="d-inline-flex" v-on="tooltipOn">
              <v-simple-checkbox
                :value="item.excluded === '' || item.excluded === null"
                :ripple="false"
                :disabled="isEditingDisabled || isReadOnlyUser"
                v-on="on"
                @click="toggleExcluded(item)"
              />
            </div>
          </template>
          <span>{{ getExclusionReason(item) }}</span>
        </v-tooltip>
      </template>
      <template #[`item.rules_applied`]="{ item }">
        <v-tooltip top :disabled="!hasRules(item.rules_applied)">
          <template #activator="{ on }">
            <v-icon v-if="hasRules(item.rules_applied)" v-on="on" color="success">
              mdi-check-circle
            </v-icon>
          </template>
          <v-card v-if="hasRules(item.rules_applied)" width="300">
            <v-card-title class="text-subtitle-2">Applied Rules</v-card-title>
            <v-divider />
            <v-list dense>
              <v-list-item v-for="(rule, index) in parseRules(item.rules_applied)" :key="index">
                <v-list-item-content class="pl-4">
                  <v-list-item-title>Rule: {{ rule.displayLabel }}</v-list-item-title>
                  <v-list-item-subtitle>Level: {{ getRuleDisplay(rule.level) }}</v-list-item-subtitle>
                  <v-list-item-subtitle v-if="rule.priority != 52">Value: {{ rule.displayValue }}</v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </v-card>
        </v-tooltip>
      </template>
      <template #[`item.enforce_compliance`]="{ item }">
        <v-tooltip :disabled="!item.enforce_compliance" top>
          <template #activator="{ on }">
            <v-icon v-on="on" :color="item.enforce_compliance ? 'green' : 'grey lighten-1'">
              mdi-shield-check-outline
            </v-icon>
            <v-icon v-if="tprProTenants && item.enforce_compliance"
              v-show="!srpIsCompliant(item)" 
              v-on="on"
              :color="getWarningColor(item)">
              mdi-exclamation-thick
            </v-icon>
          </template>
          <span>Compliance Enforced</span>
          <br>
          <span>{{ 'Variance: $' + parseFloat(item.variance).toFixed(2) }}</span>
        </v-tooltip>
      </template>
      <template #[`item.store_name`]="{ item }">
        <td class="noWrap">{{ getStoreName(item) }}</td>
      </template>
      <template #[`item.upc`]="{ item }">
        <td class="noWrap">{{ item.upc > 2 ? item.upc : '' }}</td>
      </template>
      <template #[`item.cost`]="{ item }">
        <td class="noWrap">${{ formatCurrencyDisplay(item.cost) }}</td>
      </template>
      <template #[`item.case_freight`]="{ item }">
        <td class="noWrap">${{ item.case_freight ? formatCurrencyDisplay(item.case_freight) : formatCurrencyDisplay(item.cost) }}</td>
      </template>
      <template #[`item.amap_allowance`]="{ item }">
        <td class="noWrap">
          ${{ formatCurrencyDisplay(item.amap_allowance) }}
        </td>
      </template>
      <template #[`item.dsd_oi_allowance`]="{ item }">
        <td class="noWrap">
          ${{ formatCurrencyDisplay(item.dsd_oi_allowance) }}
        </td>
      </template>
      <template #[`item.early_buy_allowance`]="{ item }">
        <td class="noWrap">
          ${{ formatCurrencyDisplay(item.early_buy_allowance) }}
        </td>
      </template>
      <template #[`item.tpr_billback_allowance`]="{ item }">
        <td class="noWrap">
          ${{ formatCurrencyDisplay(item.tpr_billback_allowance) }}
        </td>
      </template>
      <template #[`item.tpr_scan_allowance`]="{ item }">
        <td class="noWrap">
          ${{ formatCurrencyDisplay(item.tpr_scan_allowance) }}
        </td>
      </template>
      <template #[`item.tpr_scan_epay_allowance`]="{ item }">
        <td class="noWrap">
          <div class="d-flex align-center" style="white-space: nowrap;">
            <span v-if="item.tpr_scan_epay_allowance !== 'N/A'">${{ formatCurrencyDisplay(item.tpr_scan_epay_allowance) }}</span>
            <span v-else>N/A</span>
          </div>
        </td>
      </template>
      <template #[`item.tpr_scan_compete_allowance`]="{ item }">
        <td class="noWrap">
          <div class="d-flex align-center" style="white-space: nowrap;">
            <span v-if="item.tpr_scan_compete_allowance !== 'N/A'">${{ formatCurrencyDisplay(item.tpr_scan_compete_allowance) }}</span>
            <span v-else>N/A</span>
            <v-tooltip 
              v-if="item.tpr_scan_compete_allowance && item.tpr_scan_compete_allowance !== 'N/A' && item.tpr_scan_compete_allowance > 0 && $auth.tenant === 'alliance-retail-group'"
              top>
              <template #activator="{ on }">
                <v-icon 
                  v-on="on" 
                  color="blue" 
                  small 
                  class="ml-1"
                  @mouseenter="loadPromotionData(item, 'tpr_scan_compete')">
                  mdi-information
                </v-icon>
              </template>
              <div>
                <span class="font-weight-bold">TPR Scan Compete</span>
                <br>
                <span v-if="loadingPromoPrice && selectedPromotionCategory === 'tpr_scan_compete' && selectedPromotionItem === item">Loading promotion data...</span>
                <span v-else-if="promoPromotionPriceData && promoPromotionPriceData.promotion_price && selectedPromotionCategory === 'tpr_scan_compete' && selectedPromotionItem === item">
                  Promotion Price: ${{ formatCurrencyDisplay(promoPromotionPriceData.promotion_price) }}
                </span>
                <span v-else-if="selectedPromotionCategory === 'tpr_scan_compete' && selectedPromotionItem === item">No promotion price found</span>
                <span v-else>Hover to load promotion data</span>
              </div>
            </v-tooltip>
          </div>
        </td>
      </template>
      <template #[`item.edlc_billback_allowance`]="{ item }">
        <td class="noWrap">
          ${{ formatCurrencyDisplay(item.edlc_billback_allowance) }}
        </td>
      </template>
      <template #[`item.edlc_scan_allowance`]="{ item }">
        <td class="noWrap">
          ${{ formatCurrencyDisplay(item.edlc_scan_allowance) }}
        </td>
      </template>
      <template #[`item.edlc_scan_epay_allowance`]="{ item }">
        <td class="noWrap">
          <div class="d-flex align-center" style="white-space: nowrap;">
            <span v-if="item.edlc_scan_epay_allowance !== 'N/A'">${{ formatCurrencyDisplay(item.edlc_scan_epay_allowance) }}</span>
            <span v-else>N/A</span>
            <v-tooltip 
              v-if="item.edlc_scan_epay_allowance && item.edlc_scan_epay_allowance !== 'N/A' && item.edlc_scan_epay_allowance > 0 && $auth.tenant === 'alliance-retail-group'"
              top>
              <template #activator="{ on }">
                <v-icon 
                  v-on="on" 
                  color="blue" 
                  small 
                  class="ml-1"
                  @mouseenter="loadPromotionData(item, 'edlc_scan_epay')">
                  mdi-information
                </v-icon>
              </template>
              <div>
                <span class="font-weight-bold">EDLC Scan ePay</span>
                <br>
                <span v-if="loadingPromoPrice && selectedPromotionCategory === 'edlc_scan_epay' && selectedPromotionItem === item">Loading promotion data...</span>
                <span v-else-if="promoPromotionPriceData && promoPromotionPriceData.promotion_price && selectedPromotionCategory === 'edlc_scan_epay' && selectedPromotionItem === item">
                  Promotion Price: ${{ formatCurrencyDisplay(promoPromotionPriceData.promotion_price) }}
                </span>
                <span v-else-if="selectedPromotionCategory === 'edlc_scan_epay' && selectedPromotionItem === item">No promotion price found</span>
                <span v-else>Hover to load promotion data</span>
              </div>
            </v-tooltip>
          </div>
        </td>
      </template>
      <template #[`item.edlc_scan_compete_allowance`]="{ item }">
        <td class="noWrap">
          <div class="d-flex align-center" style="white-space: nowrap;">
            <span v-if="item.edlc_scan_compete_allowance !== 'N/A'">${{ formatCurrencyDisplay(item.edlc_scan_compete_allowance) }}</span>
            <span v-else>N/A</span>
            <v-tooltip 
              v-if="item.edlc_scan_compete_allowance && item.edlc_scan_compete_allowance !== 'N/A' && item.edlc_scan_compete_allowance > 0 && $auth.tenant === 'alliance-retail-group'"
              top>
              <template #activator="{ on }">
                <v-icon 
                  v-on="on" 
                  color="blue" 
                  small 
                  class="ml-1"
                  @mouseenter="loadPromotionData(item, 'edlc_scan_compete')">
                  mdi-information
                </v-icon>
              </template>
              <div>
                <span class="font-weight-bold">EDLC Scan Compete</span>
                <br>
                <span v-if="loadingPromoPrice && selectedPromotionCategory === 'edlc_scan_compete' && selectedPromotionItem === item">Loading promotion data...</span>
                <span v-else-if="promoPromotionPriceData && promoPromotionPriceData.promotion_price && selectedPromotionCategory === 'edlc_scan_compete' && selectedPromotionItem === item">
                  Promotion Price: ${{ formatCurrencyDisplay(promoPromotionPriceData.promotion_price) }}
                </span>
                <span v-else-if="selectedPromotionCategory === 'edlc_scan_compete' && selectedPromotionItem === item">No promotion price found</span>
                <span v-else>Hover to load promotion data</span>
              </div>
            </v-tooltip>
          </div>
        </td>
      </template>
      <template #[`item.unit_cost`]="{ item }">
        <td class="noWrap">${{ formatCurrencyDisplay(item.unit_cost) }}</td>
      </template>
      <template #[`item.net_unit_cost`]="{ item }">
        <td class="noWrap">${{ formatCurrencyDisplay(item.net_unit_cost) }}</td>
      </template>
      <template  #[`item.gross_profit`]="{ item }">
        {{ item.gross_profit != null ? item.gross_profit.toFixed(2) + '%' : '' }}
      </template>
      <template #[`item.net_gross_profit`]="{ item }">
        <td class="noWrap">{{ item.net_gross_profit | format }}%</td>
      </template>
      <template #[`item.retail_price`]="{ item }">
        <td class="noWrap">${{ formatCurrencyDisplay(item.retail_price) }}</td>
      </template>
      <template #[`item.unit_savings`]="{ item }">
        <td class="noWrap">${{ formatCurrencyDisplay(calculateUnitSavings(item)) }}</td>
      </template>
      <template #[`item.obxi`]="{ item }">
        <td class="noWrap">{{ item.obxi }}</td>
      </template>
      <template #[`item.like_code`]="{ item }">
        <td class="noWrap">{{ item.like_code }}</td>
      </template>
      <template #[`item.linked_group`]="{ item }">
        <td class="noWrap">
          <span 
            v-if="item.linked_group && showLinkedGroupLink" 
            class="primary--text cursor-pointer text-decoration-underline"
            @click="filterByLinkedGroup(item.linked_group)"
            :title="`Filter by Linked Group: ${item.linked_group}`">
            {{ item.linked_group }}
          </span>
          <span v-else>{{ item.linked_group || '' }}</span>
        </td>
      </template>
      <template #[`item.start_dt`]="{ item }">
        <td class="noWrap">{{ formatDateDisplay(item.start_dt) }}</td>
      </template>
      <template #[`item.end_dt`]="{ item }">
        <td class="noWrap">{{ formatDateDisplay(item.end_dt) }}</td>
      </template>
      <template #[`item.updated_on`]="{ item }">
        <td class="noWrap">{{ formatDateTimestamp(item.updated_on) }}</td>
      </template>
      <template #[`item.rules_promotion_price`]="{ item }">
        <td class="noWrap" v-if="item.rules_promotion_price">
          ${{ formatCurrencyDisplay(item.rules_promotion_price) }}
        </td>
      </template>
      <template #[`item.original_promotion_price`]="{ item }">
        <td class="noWrap" v-if="item.original_promotion_price">
          ${{ formatCurrencyDisplay(item.original_promotion_price) }}
        </td>
      </template>
      <template v-for="index in compPricingModes" v-slot:[`item.comp_pricing_${index}`]="{ item }">
        <td class="noWrap" :key="index">
          <div class="d-flex align-center">
            <v-tooltip v-if="getCompetitorPrice(item, index) !== 'N/A'" bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-icon v-if="tprProTenants" v-bind="attrs" v-on="on" :color="getPriceComparisonColor(item, index)">
                  {{ getPriceComparisonIcon(item, index) }}
                </v-icon>
              </template>
              <span>{{ getPriceComparisonText(item, index) }}</span>
            </v-tooltip>
            <span class="ml-2">
              <span v-if="getCompetitorPrice(item, index) !== 'N/A'">${{ formatCurrencyDisplay(getCompetitorPrice(item, index)) }}</span>
              <span v-else>N/A</span>
            </span>
          </div>
        </td>
      </template>
    </v-data-table>
    <LinkedGroupModal 
      v-if="linkedGroupModal"
      :value.sync="linkedGroupModal" 
      :item="linkedGroupItem" 
      :batch="selectedBatch" 
      :batchItems="batchItems"
      :store="store"
      :isTprProRetailUser="isTprProRetailUser"
      :storeGroupIsSelected="storeGroupIsSelected"
      @onSave="linkedGroupUpdate">
    </LinkedGroupModal>
  </v-container>
</td>
</template>
<script>
import { cloneDeep } from 'lodash'
import Rules from '@/axios/rules-management.js'
import { utils } from '@/mixins/utils'
import { notification } from '@/mixins/notification'
import { displayHelpers } from '@/mixins/displayHelpers'
import { userAccess } from '@/mixins/user-access'
import { tprItemRow } from '@/mixins/tprItemRow'
import LinkedGroupModal from '@/components/LinkedGroupModal.vue'
export default {
  name: 'BatchItemAlternateStores',
  props: {
    headers: Array,
    stores: Array,
    item: Object,
    alternateStoreItems: Array,
    parentCompPricingModes: Array,
    selectedBatch: Object,
    batchItems: Array,
    parentColumnWidths: Array,
    isEditingDisabled: Boolean,
    storeGroupIsSelected: Boolean
  },
  components: { LinkedGroupModal },
  mixins: [utils, displayHelpers, notification, userAccess, tprItemRow],
  data() {
    return {
      loading: false,
      store: null,
      localItems: [],
      linkedGroupItem: null,
      linkedGroupModal: false,
      rowClassName: '',
      columnWidthStyleId: ''
    }
  },
  created() {
    this.localItems = cloneDeep(this.alternateStoreItems)
    this.rowClassName = `alternate-store-row-${this._uid}`
    this.columnWidthStyleId = `alternate-store-widths-${this._uid}`
    this.attachCompPricingToLocalItems()
  },
  mounted() {
    this.applyHeaderWidthsToItemClass()
  },
  beforeDestroy() {
    this.cleanupHeaderWidthStyles()
  },
  watch: {
    alternateStoreItems: {
      handler(newValue) {
        this.localItems = cloneDeep(newValue)
        this.attachCompPricingToLocalItems()
      }
    },
    alternateStoreHeaders() {
      this.applyHeaderWidthsToItemClass()
    },
    parentColumnWidths() {
      this.applyHeaderWidthsToItemClass()
    }
  },
  computed: {
    alternateStoreHeaders() {
      const localHeaders = cloneDeep(this.headers || [])
      if (Array.isArray(this.parentColumnWidths) && this.parentColumnWidths.length) {
        localHeaders.forEach((header, index) => {
          const width = this.parentColumnWidths[index]
          if (width !== undefined && width !== null && width !== '') {
            header.width = String(width)
          }
        })
      }
      return localHeaders
    },
    tprProTenants() {
      return this.$auth.tenant === 'alliance-retail-group'
    },
    changes() {
      return this.localItems.filter(item => {
        const originalItem = this.alternateStoreItems.find(origItem => origItem.id === item.id)
        const matchingPromotionPrice = Number(originalItem.promotion_price) === Number(item.promotion_price)
        const matchingMultiplier = Number(originalItem.multiplier) === Number(item.multiplier)
        return (!matchingPromotionPrice || !matchingMultiplier)
      })
    },
    compPricingModes() {
      if (this.$auth.tenant !== 'alliance-retail-group') return []
      const visibleCompetitors = this.alternateStoreHeaders
        .filter(h => /^comp_pricing_/.test(h.value))
        .map(h => h.value.replace(/^comp_pricing_/, ''))
      if (Array.isArray(this.parentCompPricingModes) && this.parentCompPricingModes.length) {
        return this.parentCompPricingModes.filter(mode => visibleCompetitors.includes(mode))
      }
      return visibleCompetitors
    }
  },
  methods: {
    calculateUnitSavings(item) {
      const multiplier = Number(item.multiplier) || 1
      if (multiplier <= 0) return 0
      const perUnitSrp = (Number(item.promotion_price) || 0) / multiplier
      return (Number(item.retail_price) || 0) - perUnitSrp
    },
    onSubItemChange(item, field, value) {
      if (this.isEditingDisabled) return
      this.$emit('subItemChange', { id: item.id, field, value, item_id: item.item_id, item })
    },
    onAltSrpBlur(item) {
      if (this.srpExceedsBaseRetail(item)) {
        this.alert('Item cannot be included: its TPR SRP exceeds base retail.')
      }
    },
    srpFieldBg(item) {
      return this.srpExceedsBaseRetail(item) ? 'amber lighten-5' : undefined
    },
    handleEnterKey(currentRowIndex, fieldName, event) {
      event.preventDefault()
      let nextRow = currentRowIndex + 1
      let nextField = document.querySelector(
        `[data-row="${nextRow}"][data-field="${fieldName}"]`
      )
      while (nextField && nextField?.hasAttribute('disabled')) {
        nextRow += 1
        nextField = document.querySelector(
          `[data-row="${nextRow}"][data-field="${fieldName}"]`
        )
      }
      nextField?.focus()
      nextField?.select()
      this.updateItems()
    },
    async updateItems() {
      if (this.isEditingDisabled) return
      let payloads = []
      this.changes.forEach(changedItem => {
        if (changedItem && changedItem.enforce_compliance && changedItem.multiplier && changedItem.promotion_price && !this.srpIsCompliant(changedItem)) {
          this.alert(`Item is non-compliant: ${changedItem.id}`)
          return
        }
        payloads.push({
          id: changedItem.id,
          promotion_price: changedItem.promotion_price,
          multiplier: changedItem.multiplier,
          note: changedItem.note,
          net_gross_profit: changedItem.net_gross_profit
        })
      })
      if (!payloads.length) return
      const success = await this.storeSrpMultiplierUpdate(payloads)
      this.$emit('alternateItemsUpdate', this.item.id, this.localItems)
      if (success) this.$emit('subItemsSaved', payloads.map(p => p.id))
    },
    linkedGroupUpdate (changedItems) {
      this.$emit('linkedGroupUpdate', changedItems, this.store)
    },
    async storeSrpMultiplierUpdate(payloads) {
      this.loading = true
      let success = false
      try {
        const response = await Rules.storeSrpMultiplier(payloads)
        if (response.data && response.data.message) {
          if (response.data.message === 'TPR successfully updated.') {
            this.notify(response.data.message)
            success = true
          } else {
            this.alert(response.data.message)
          }
        } else {
          this.notify('Item(s) updated successfully')
          success = true
        }
      } catch (err) {
        const message = err?.response?.data?.message || err.message
        this.alert(`Error updating store items: ${message}`)
      } finally {
        this.loading = false
      }
      return success
    },
    async showLinkedGroupModal(item) {
      if (item.promotion_price <= 0 || item.multiplier <= 0) {
        this.alert('SRP and Multiplier must be greater than zero.')
        return false
      }
      this.store = this.stores.find(s => s.store_party_id === item.store_id)
      this.linkedGroupItem = item
      this.linkedGroupModal = true
    },
    async toggleExcluded(item) {
      if (this.isEditingDisabled || this.isReadOnlyUser) return
      const currentlyExcluded = !(item.excluded === '' || item.excluded === null)
      if (currentlyExcluded && this.srpExceedsBaseRetail(item)) {
        this.alert('Item cannot be included: its TPR SRP exceeds base retail.')
        return
      }
      try {
        if (item.excluded === '' || item.excluded === null) item.excluded = 'manual'
        else if (item.excluded) item.excluded = ''
        else if (item.excluded === 'manual') item.excluded = ''
        await Rules.saveExclusion(item.id, this.$auth.user.email, item.excluded)
      } catch (error) {
        console.error('Error while toggling exclusion: ', error)
      }
    },
    resetRow(item) {
      try {
        const originalItem = this.alternateStoreItems.find(origItem => origItem.id === item.id)
        if (originalItem) {
          this.$set(item, 'multiplier', originalItem.multiplier)
          this.$set(item, 'promotion_price', originalItem.promotion_price)
          this.$set(item, 'net_gross_profit', originalItem.net_gross_profit)
          const itemIndex = this.localItems.findIndex(i => i.id === item.id)
          if (itemIndex !== -1) {
            this.$set(this.localItems, itemIndex, { ...this.localItems[itemIndex], multiplier: originalItem.multiplier, promotion_price: originalItem.promotion_price })
          }
        }
      } catch (err) {
        console.error(err)
        const message = err?.response?.data?.message || err.message
        this.alert(`Error resetting values: ${message}`)
      }
    },
    getStoreName(item) {
      const store = this.stores.find(s => s.store_party_id === item.store_id)
      return store ? store.store_name : item.store_name
    },
    attachCompPricingToLocalItems() {
      if (!Array.isArray(this.localItems)) return
      const modes = this.compPricingModes || []
      modes.forEach(mode => {
        const key = `comp_pricing_${mode}`
        this.localItems.forEach(li => {
          if (li[key] === undefined) {
            const source = this.item && this.item[key] !== undefined ? this.item[key] : 'N/A'
            this.$set(li, key, source)
          }
        })
      })
    },
    getItemClass() {
      return [this.rowClassName, 'grey lighten-5']
    },
    applyHeaderWidthsToItemClass() {
      if (!this.rowClassName || !this.columnWidthStyleId) return
      let style = document.getElementById(this.columnWidthStyleId)
      if (!style) {
        style = document.createElement('style')
        style.id = this.columnWidthStyleId
        document.head.appendChild(style)
      }
      const rules = this.alternateStoreHeaders
        .map((header, index) => {
          if (!header.width) return ''
          const widthValue = this.normalizeWidth(header.width)
          return `.${this.rowClassName} td:nth-child(${index + 1}) { min-width: ${widthValue}; max-width: ${widthValue}; }`
        })
        .filter(Boolean)
        .join('\n')
      style.textContent = rules
    },
    normalizeWidth(rawWidth) {
      if (rawWidth === null || rawWidth === undefined || rawWidth === '') return ''
      const width = String(rawWidth).trim()
      if (/^\d+$/.test(width) || /^\d+\.\d+$/.test(width)) {
        return `${width}px`
      }
      return width
    },
    cleanupHeaderWidthStyles() {
      const style = document.getElementById(this.columnWidthStyleId)
      if (style && style.parentNode) {
        style.parentNode.removeChild(style)
      }
    },
  }
}
</script>
<style>
.noWrap {
  white-space: nowrap;
}
</style>
